import React, { useEffect, useImperativeHandle } from "react";
import useMeasure from "react-use-measure";
import { animated, useSpringValue } from "@react-spring/web";
import cx from "classnames";

import { ScrollableContainer } from "../scroller";
import styles from "./tabbar.module.scss";

interface TabBarProps {
  className?: string;
  children: React.ReactNode;
  active: any;
  gap: number;
  tabSize?: "auto" | "equal";
  // eslint-disable-next-line no-unused-vars
  onSelectionChange: (index: number) => void;
  showInitialAnimation?: boolean;
  scrollIntoView?: boolean;
  innerClass?: string;
}

const TabBar = React.forwardRef<any, TabBarProps>(
  (
    {
      className,
      children,
      active,
      tabSize = "auto",
      gap = 32,
      onSelectionChange,
      showInitialAnimation = false,
      scrollIntoView = false,
      innerClass
    },
    ref,
  ) => {
    const indicatorAnimations = useSpringValue(0);
    const tabBarRef = React.useRef<HTMLDivElement>(null);
    const [tabItemRef, bounds] = useMeasure();

    useImperativeHandle(ref, () => {
      return {
        refresh: () => {
          calculateIndicatorPosition();
        },
      };
    }, [active]);

    useEffect(() => {
      calculateIndicatorPosition();
      if (scrollIntoView) {
        scrollActiveTabIntoView();
      }
    }, [indicatorAnimations, active, bounds]);

    const scrollActiveTabIntoView = () => {
      if (!tabBarRef.current) return;

      const tabs = tabBarRef.current.querySelectorAll(`.${CSS.escape(styles.tab)}`);
      const activeTab = tabs[active] as HTMLElement;

      if (activeTab && activeTab.scrollIntoView) {
        activeTab.scrollIntoView({
          behavior: "smooth",
          inline: "center",
          block: "nearest",
        });
      }
    };

    const calculateIndicatorPosition = () => {
      if (!tabBarRef.current) {
        return;
      }

      const tabbar = tabBarRef.current;
      const tabs = tabBarRef.current.querySelectorAll(
        `.${CSS.escape(styles.tab)}`,
      );
      const tab = tabs[active] as HTMLElement;
      if (!tab || !tabbar) {
        return;
      }

      const boundingRect = tab.getBoundingClientRect();
      const scrollLeft = tab.parentElement?.scrollLeft || 0;
      const left = scrollLeft + tab.offsetLeft;
      const width = boundingRect.width;

      const offset = left + (width - 30) / 2;

      indicatorAnimations.start(offset);
    };

    return (
      <div
        ref={tabBarRef}
        className={cx(styles.tabBar, [styles[tabSize]], className)}
      >
        <ScrollableContainer
          distance={showInitialAnimation ? 300 : 100}
          leftArrowComponent={showInitialAnimation ? LeftArrow : undefined}
          rightArrowComponent={showInitialAnimation ? RightArrow : undefined}
          showInitialAnimation={showInitialAnimation}
        >
          <div
            className={cx(styles.inner, {
              [styles.single]: React.Children.count(children) === 1}, innerClass
            )}
            style={{ gap: `${gap}px` }}
          >
            {React.Children.map(children, (child, index) => {
              const component = React.isValidElement(child)
                ? React.cloneElement(child)
                : child;
              return (
                <div
                  ref={tabItemRef}
                  className={cx(styles.tab)}
                  onClick={() => onSelectionChange(index)}
                >
                  {component}
                </div>
              );
            })}
            <animated.div
              className={styles.indicator}
              style={{ left: indicatorAnimations }}
            />
          </div>
        </ScrollableContainer>
      </div>
    );
  },
);

enum ScrollArrowPosition {
  "Left",
  "Right",
}

interface ArrowProps {
  position: ScrollArrowPosition;
  scrollAvailable: boolean;
}

const Arrow: React.FC<ArrowProps> = ({ position, scrollAvailable }) => {
  return (
    <div
      className={cx(styles.arrowContainer, {
        [styles.left]: position === ScrollArrowPosition.Left,
        [styles.right]: position === ScrollArrowPosition.Right,
        [styles.scrollAvailable]: scrollAvailable,
      })}
    >
      <div className={styles.arrow}><i className="paperflite-arrow-up"></i></div>
    </div>
  );
};

const LeftArrow: React.FC<any> = ({ scrollAvailable }) => {
  return (
    <Arrow position={ScrollArrowPosition.Left} scrollAvailable={scrollAvailable} />
  );
};

const RightArrow: React.FC<any> = ({ scrollAvailable }) => {
  return (
    <Arrow position={ScrollArrowPosition.Right} scrollAvailable={scrollAvailable} />
  );
};

TabBar.displayName = "TabBar";

export { TabBar };
